/**
 * Technical-error reporting shared by every app.
 *
 * Servers write one JSON line per error to stderr via `logTechnicalError`.
 * Elastic Beanstalk streams stdout/stderr to CloudWatch Logs, so each line is
 * searchable there (CloudWatch Logs Insights parses the JSON fields).
 *
 * Browser clients POST the same shape to `CLIENT_ERROR_REPORT_PATH` on their
 * own server so browser-side failures (wallet/contract calls, fetch failures)
 * land in the same log group.
 *
 * Every report carries a short reference (`DFL-XXXXXXXX`). The user-facing
 * message asks the user to post that reference in the Discord bug channel so a
 * report can be matched to its log line.
 */

export const CLIENT_ERROR_REPORT_PATH = '/api/client-errors';
export const CLIENT_ERROR_REPORT_MAX_BYTES = 16 * 1024;
export const BUG_REPORT_CHANNEL = '#feedback-bugs';
export const ERROR_REFERENCE_PATTERN = /^DFL-[A-Z0-9]{8}$/;

export type ServerErrorSource = 'franchise-api' | 'simulator';
export type ClientErrorSource = 'franchise-ui' | 'simulator-client';
export type ErrorReportSource = ServerErrorSource | ClientErrorSource;

export type SerializedError = {
  name: string;
  message: string;
  stack?: string;
};

export type ClientErrorReport = {
  source: ClientErrorSource;
  reference: string;
  context: string;
  error: SerializedError;
  url?: string;
  details?: Record<string, string | number | boolean | null>;
};

export type TechnicalErrorLogEntry = {
  source: ErrorReportSource;
  reference: string;
  /** Short machine-friendly label for where the failure happened, e.g. `depth-chart-save`. */
  context: string;
  error?: unknown;
  url?: string;
  details?: Record<string, unknown>;
  request?: {
    method?: string;
    path?: string;
    ip?: string;
    userAgent?: string;
  };
};

const MAX_CONTEXT_LENGTH = 200;
const MAX_MESSAGE_LENGTH = 1_000;
const MAX_STACK_LENGTH = 4_000;
const MAX_URL_LENGTH = 500;
const MAX_DETAIL_KEYS = 25;
const MAX_DETAIL_VALUE_LENGTH = 500;
const CLIENT_SOURCES: readonly ClientErrorSource[] = ['franchise-ui', 'simulator-client'];

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function randomHex(bytes: number): string {
  const cryptoApi = (globalThis as { crypto?: { getRandomValues?: (array: Uint8Array) => Uint8Array } }).crypto;
  const buffer = new Uint8Array(bytes);
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(buffer);
  } else {
    // Error references only need to be unlikely to collide, not unpredictable.
    for (let index = 0; index < buffer.length; index += 1) {
      buffer[index] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(buffer, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Short reference a user can quote when reporting a failure. */
export function createErrorReference(): string {
  return `DFL-${randomHex(4).toUpperCase()}`;
}

export function isErrorReference(value: unknown): value is string {
  return typeof value === 'string' && ERROR_REFERENCE_PATTERN.test(value);
}

/** Shape any thrown value into a JSON-safe error record. */
export function serializeError(error: unknown): SerializedError {
  if (error instanceof Error) {
    const extra = error as Error & { code?: unknown; reason?: unknown; shortMessage?: unknown; status?: unknown };
    const messageParts = [error.message];
    if (typeof extra.reason === 'string' && extra.reason && extra.reason !== error.message) {
      messageParts.push(`reason: ${extra.reason}`);
    } else if (typeof extra.shortMessage === 'string' && extra.shortMessage && extra.shortMessage !== error.message) {
      messageParts.push(`shortMessage: ${extra.shortMessage}`);
    }
    if (extra.code !== undefined && extra.code !== null) {
      messageParts.push(`code: ${String(extra.code)}`);
    }
    if (typeof extra.status === 'number') {
      messageParts.push(`status: ${extra.status}`);
    }
    return {
      name: error.name || 'Error',
      message: truncate(messageParts.join(' | '), MAX_MESSAGE_LENGTH),
      ...(error.stack ? { stack: truncate(error.stack, MAX_STACK_LENGTH) } : {}),
    };
  }
  if (typeof error === 'object' && error !== null) {
    // Plain error-shaped objects: EIP-1193 provider errors, and already
    // serialized errors arriving from a browser report.
    const record = error as { name?: unknown; message?: unknown; stack?: unknown; code?: unknown; reason?: unknown };
    const message = typeof record.message === 'string' && record.message
      ? record.message
      : typeof record.reason === 'string' && record.reason
        ? record.reason
        : safeStringify(error);
    return {
      name: typeof record.name === 'string' && record.name ? record.name : 'Error',
      message: truncate(record.code !== undefined ? `${message} | code: ${String(record.code)}` : message, MAX_MESSAGE_LENGTH),
      ...(typeof record.stack === 'string' && record.stack ? { stack: truncate(record.stack, MAX_STACK_LENGTH) } : {}),
    };
  }
  return { name: 'Error', message: truncate(String(error), MAX_MESSAGE_LENGTH) };
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/**
 * The message shown to a user when a technical failure happens. `summary` is
 * the caller's plain-language description of what did not work.
 */
export function userFacingErrorMessage(summary: string, reference: string): string {
  const lead = summary.trim().replace(/\s+$/, '');
  const sentence = /[.!?]$/.test(lead) ? lead : `${lead}.`;
  return `${sentence} Please post this in the ${BUG_REPORT_CHANNEL} channel on Discord with reference ${reference}.`;
}

function normalizeDetails(value: unknown): Record<string, string | number | boolean | null> | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined;
  }
  const details: Record<string, string | number | boolean | null> = {};
  let count = 0;
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (count >= MAX_DETAIL_KEYS) {
      break;
    }
    const safeKey = truncate(key, 64);
    if (raw === null || typeof raw === 'number' || typeof raw === 'boolean') {
      details[safeKey] = raw;
    } else if (typeof raw === 'string') {
      details[safeKey] = truncate(raw, MAX_DETAIL_VALUE_LENGTH);
    } else if (raw !== undefined) {
      details[safeKey] = truncate(safeStringify(raw), MAX_DETAIL_VALUE_LENGTH);
    } else {
      continue;
    }
    count += 1;
  }
  return count > 0 ? details : undefined;
}

/**
 * Validate and bound a browser-submitted error report. Returns `null` when the
 * payload is not a report at all; otherwise every string is truncated so a
 * hostile client cannot flood the log.
 */
export function normalizeClientErrorReport(payload: unknown): ClientErrorReport | null {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    return null;
  }
  const record = payload as Record<string, unknown>;
  if (!CLIENT_SOURCES.includes(record.source as ClientErrorSource)) {
    return null;
  }
  if (typeof record.context !== 'string' || !record.context.trim()) {
    return null;
  }
  const rawError = record.error;
  if (typeof rawError !== 'object' || rawError === null) {
    return null;
  }
  const errorRecord = rawError as Record<string, unknown>;
  if (typeof errorRecord.message !== 'string') {
    return null;
  }

  return {
    source: record.source as ClientErrorSource,
    reference: isErrorReference(record.reference) ? record.reference : createErrorReference(),
    context: truncate(record.context.trim(), MAX_CONTEXT_LENGTH),
    error: {
      name: typeof errorRecord.name === 'string' && errorRecord.name ? truncate(errorRecord.name, 100) : 'Error',
      message: truncate(errorRecord.message, MAX_MESSAGE_LENGTH),
      ...(typeof errorRecord.stack === 'string' && errorRecord.stack
        ? { stack: truncate(errorRecord.stack, MAX_STACK_LENGTH) }
        : {}),
    },
    ...(typeof record.url === 'string' && record.url ? { url: truncate(record.url, MAX_URL_LENGTH) } : {}),
    ...(normalizeDetails(record.details) ? { details: normalizeDetails(record.details) } : {}),
  };
}

/**
 * Write one structured JSON line to stderr. On Elastic Beanstalk this lands in
 * `web.stdout.log`, which streams to CloudWatch Logs. Search by `reference` to
 * find the line behind a user's bug report.
 */
export function logTechnicalError(entry: TechnicalErrorLogEntry): void {
  const line = {
    level: 'error',
    type: 'technical-error',
    timestamp: new Date().toISOString(),
    source: entry.source,
    reference: entry.reference,
    context: truncate(entry.context, MAX_CONTEXT_LENGTH),
    ...(entry.error !== undefined ? { error: serializeError(entry.error) } : {}),
    ...(entry.url ? { url: truncate(entry.url, MAX_URL_LENGTH) } : {}),
    ...(entry.request ? { request: entry.request } : {}),
    ...(entry.details ? { details: normalizeDetails(entry.details) } : {}),
  };
  console.error(JSON.stringify(line));
}

/**
 * Error thrown by browser API clients when a request returns a non-2xx status.
 * `status` lets callers tell an explained business failure (4xx — show the
 * server's message) from a technical one (5xx/network — log and ask the user
 * to report it). `reference` carries the server's log reference when a 500
 * response included one, so the client report correlates with the server line.
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly reference: string | null;

  constructor(message: string, status: number, reference: string | null = null) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.reference = reference;
  }

  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }
}

/** Build an `ApiRequestError` from a failed `fetch` response body. */
export async function apiRequestErrorFromResponse(
  response: { status: number; json(): Promise<unknown> },
  fallbackMessage = 'Request failed.',
): Promise<ApiRequestError> {
  const payload = await response.json().catch(() => null);
  const record = payload && typeof payload === 'object' ? payload as { error?: unknown; reference?: unknown } : null;
  const message = record && record.error !== undefined && record.error !== null && String(record.error)
    ? String(record.error)
    : fallbackMessage;
  return new ApiRequestError(message, response.status, isErrorReference(record?.reference) ? record.reference : null);
}

/**
 * A failure the user can act on themselves (a missing wallet extension, an
 * unverified session). `reportError` shows its message verbatim and does not
 * log it, because there is nothing for an engineer to fix.
 */
export class ExpectedFailure extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ExpectedFailure';
  }
}
